import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { MediaClient } from '@confluence/media-client';

type MediaStore = MediaClient['mediaStore'];

import { parseSheetNames } from '../HtmlSheetViewer.tsx';
import { i18n } from '../viewXlsMacroI18n.ts';
import { toError } from '../macroErrorUtils.ts';

// The Atlassian Media Service generates a 'document.html' artifact when processing Excel
// files — an HTML rendering of the spreadsheet content.
const DOCUMENT_HTML_ARTIFACT = 'document.html' as const;

type NullableString = string | null | undefined;

const EMPTY_SHEET_NAMES: string[] = [];

const createExcelMediaStatusError = (message: string): Error => {
	const error = new Error(message);
	error.name = 'ExcelMediaStatusError';
	return error;
};

const isExcelMediaStatusError = (error: Error): boolean => error.name === 'ExcelMediaStatusError';

async function fetchExcelHtmlArtifact(
	mediaStore: MediaStore,
	mediaFileId: string,
	collectionName: NullableString,
	errorMessages: { pending: string; failed: string },
): Promise<{ text: () => Promise<string> } | null> {
	const { data: mediaFile } = await mediaStore.getFile(mediaFileId, {
		collection: collectionName ?? undefined,
	});

	const processingStatus = mediaFile.processingStatus as string | undefined;
	if (processingStatus === 'pending' || processingStatus === 'running') {
		throw createExcelMediaStatusError(errorMessages.pending);
	}
	if (processingStatus === 'failed') {
		throw createExcelMediaStatusError(errorMessages.failed);
	}

	const htmlArtifact = mediaFile.artifacts?.[DOCUMENT_HTML_ARTIFACT] as
		| { processingStatus?: string }
		| undefined;
	if (!htmlArtifact || htmlArtifact.processingStatus !== 'succeeded') {
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	return mediaStore.getArtifactBinary(mediaFile.artifacts!, DOCUMENT_HTML_ARTIFACT, {
		collectionName: collectionName ?? undefined,
	});
}

interface UseExcelHtmlContentParams {
	isDisplayMode: boolean;
	isExcelAttachment: boolean;
	mediaClient: MediaClient | null | undefined;
	mediaFileId: NullableString;
	collectionName: NullableString;
	attachmentName: NullableString;
}

interface UseExcelHtmlContentResult {
	isLoading: boolean;
	htmlContent: string | null;
	sheetNames: string[];
	shouldShowNotice: boolean;
	error: Error | null;
}

/**
 * Fetches the HTML artifact generated by the media service for an Excel attachment
 * and parses the sheet names from it.
 *
 * Returns loading/content/notice/error state. The caller is responsible for
 * rendering the appropriate UI based on these values.
 */
export function useExcelHtmlContent({
	isDisplayMode,
	isExcelAttachment,
	mediaClient,
	mediaFileId,
	collectionName,
	attachmentName,
}: UseExcelHtmlContentParams): UseExcelHtmlContentResult {
	const { formatMessage } = useIntl();
	const [isLoading, setIsLoading] = useState(
		!(!isDisplayMode || !isExcelAttachment || !mediaClient || !mediaFileId),
	);
	const [htmlContent, setHtmlContent] = useState<string | null>(null);
	const [sheetNames, setSheetNames] = useState<string[]>([]);
	const [shouldShowNotice, setShouldShowNotice] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => {
		let isCancelled = false;

		if (!isDisplayMode || !isExcelAttachment || !mediaClient || !mediaFileId) {
			setIsLoading(false);
			setShouldShowNotice(false);
			setHtmlContent(null);
			setSheetNames(EMPTY_SHEET_NAMES);
			setError(null);
			return;
		}

		const excelAttachmentName = attachmentName ?? '';

		setIsLoading(true);
		setShouldShowNotice(false);
		setHtmlContent(null);
		setSheetNames(EMPTY_SHEET_NAMES);
		setError(null);

		void fetchExcelHtmlArtifact(mediaClient.mediaStore, mediaFileId, collectionName, {
			pending: formatMessage(i18n.errorStillProcessing, { filename: excelAttachmentName }),
			failed: formatMessage(i18n.errorFailedToRetrieve, { filename: excelAttachmentName }),
		})
			.then(async (htmlArtifactBlob) => {
				if (!htmlArtifactBlob) {
					if (!isCancelled) {
						setShouldShowNotice(true);
						setHtmlContent(null);
						setSheetNames(EMPTY_SHEET_NAMES);
					}
					return;
				}

				const content = await htmlArtifactBlob.text();
				const parsedSheetNames = parseSheetNames(content);

				if (isCancelled) {
					return;
				}

				if (content && parsedSheetNames.length > 0) {
					setHtmlContent(content);
					setSheetNames(parsedSheetNames);
					setShouldShowNotice(false);
				} else {
					setShouldShowNotice(true);
					setHtmlContent(null);
					setSheetNames(EMPTY_SHEET_NAMES);
				}
			})
			.catch((fetchError) => {
				if (!isCancelled) {
					const normalizedError = toError(fetchError);
					if (isExcelMediaStatusError(normalizedError)) {
						setError(normalizedError);
					} else {
						setShouldShowNotice(true);
					}
					setHtmlContent(null);
					setSheetNames(EMPTY_SHEET_NAMES);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [
		attachmentName,
		collectionName,
		formatMessage,
		isDisplayMode,
		isExcelAttachment,
		mediaClient,
		mediaFileId,
	]);

	return { isLoading, htmlContent, sheetNames, shouldShowNotice, error };
}
